import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { FilterChip } from '../../../../components/design-system/common/FilterChip';

import { AppearanceFilters } from '../../../../common/hooks/use-product-insights.hook';
import { CatalogFacets as CatalogFacetCounts } from '../../../../common/models/catalog-query.model';

/** The three groups worth showing. Finish exists too, but it is a long tail. */
const GROUPS = ['colorFamily', 'materialFamily', 'style'] as const;
type Group = (typeof GROUPS)[number];

/** Enough to see the shape of a catalogue without becoming a wall of chips. */
const SHOWN_PER_GROUP = 6;

/**
 * What the catalogue is made of, as filters.
 *
 * Replaces three stat cards, two of which were hardcoded em-dashes that never
 * got wired. A count of products is not worth a card — the list underneath
 * already shows it — but what those products ARE is not visible anywhere else,
 * and it is the only thing here a vendor cannot work out for themselves.
 *
 * Counted from the catalogue rather than the vocabulary, so a value nobody
 * stocks is never offered: an empty filter is a dead end dressed as a choice.
 * The counting happens on the server, most common first.
 */
export const CatalogFacets: React.FC<{
    appearance: CatalogFacetCounts['appearance'];
    /** Products in the catalogue, before any filter. */
    total: number;
    value: AppearanceFilters;
    onChange: (next: AppearanceFilters) => void;
    /** How many survive the filters, so the header can say what a click left. */
    shown: number;
}> = ({ appearance, total, value, onChange, shown }) => {
    const { t } = useTranslation();

    const counts = useMemo(
        () => ({
            colorFamily: appearance.colorFamily ?? [],
            materialFamily: appearance.materialFamily ?? [],
            style: appearance.style ?? [],
        }),
        [appearance],
    );
    const analysed = appearance.analysed ?? 0;

    const filtering =
        value.colorFamily.length > 0 ||
        value.materialFamily.length > 0 ||
        value.style.length > 0 ||
        value.finish.length > 0;

    // Nothing has been analysed: no chips, no empty shell where they would be.
    if (analysed === 0) return null;

    const toggle = (group: Group, entry: string) => {
        const current = value[group];
        onChange({
            ...value,
            [group]: current.includes(entry) ? current.filter((v) => v !== entry) : [...current, entry],
        });
    };

    return (
        <div className="mb-6 flex flex-col gap-3 rounded-xl border bg-card p-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="text-sm font-medium">{t('PAGES.MY_PRODUCTS.FACETS.TITLE')}</p>
                {/*
                  One live number instead of a count on every chip, with its
                  undo beside it.

                  Eighteen counts competed with eighteen labels while saying
                  something the ordering already says — the chips are sorted by
                  frequency, so rank was encoded twice. And none of them
                  answered the question a person actually has, which arrives
                  only after clicking: what am I left with?

                  "Clear" belongs next to that number. It used to hang below the
                  last row of chips as bare underlined text, where it read as a
                  stray link and sat close enough to STYLE to look like one of
                  its values.
                */}
                <div className="flex items-center gap-2">
                    <p className="text-xs tabular-nums text-muted-foreground">
                        {filtering
                            ? t('PAGES.MY_PRODUCTS.FACETS.SHOWING', { shown, total })
                            : t('PAGES.MY_PRODUCTS.FACETS.ANALYSED', { analysed, total })}
                    </p>
                    {filtering && (
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={() => onChange({ colorFamily: [], materialFamily: [], style: [], finish: [] })}
                        >
                            <X data-icon="inline-start" />
                            {t('PAGES.MY_PRODUCTS.FACETS.CLEAR')}
                        </Button>
                    )}
                </div>
            </div>

            {GROUPS.map((group) =>
                counts[group].length === 0 ? null : (
                    <div key={group} className="flex flex-wrap items-center gap-x-1.5 gap-y-2">
                        {/*
                          Smaller and lettered wide, against chips a size up.
                          Both were text-xs, which gave the eye nothing to
                          separate the question from its answers.
                        */}
                        <span className="me-2 w-16 shrink-0 text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
                            {t(`PAGES.MY_PRODUCTS.FACETS.${group.toUpperCase()}`)}
                        </span>
                        {counts[group].slice(0, SHOWN_PER_GROUP).map(([entry]) => (
                            <FilterChip
                                key={entry}
                                size="md"
                                selected={value[group].includes(entry)}
                                onClick={() => toggle(group, entry)}
                                label={t(
                                    `PAGES.MY_PRODUCTS.APPEARANCE.${entry.toUpperCase().replace('-', '_')}`,
                                    entry,
                                )}
                            />
                        ))}
                    </div>
                ),
            )}

        </div>
    );
};
